import React from "react";
import { Badge, Button, Modal, Form, Alert, Spinner } from "react-bootstrap";
import { FiAlertTriangle, FiSend, FiChevronDown } from "react-icons/fi";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import {
  formatReportTagPeriodSuffix,
  getRequestReviewCopy,
  getWorkflowStatusLabel,
  getWorkflowStatusVariant,
  resolveDepartmentReportDisplayStatus,
  type NeedsAttentionDetails,
} from "@core/reports/utils/reportWorkflow";
import { formatReportTagDeadline } from "@features/report/tag/utils/reportTagDeadline";
import { subtleBadgeProps } from "@core/utils/subtleBadge";
import { useAuthStore } from "@features/auth/store/authStore";
import NeedsAttentionDetailsList from "@core/reports/components/NeedsAttentionDetailsList";

export type ReviewEligibility = {
  eligible: boolean;
  reasons?: string[];
};

export type ReviewRequestPayload = {
  reason: string;
};

export type ReportActionsPanelMeta = {
  scopeLabel: string; //Nation | District | Church
  scopeName: string; // Jamaica | Aenon Town District | Aenon Town NTCOG
  departmentName?: string | null;
  reportTagName?: string | null;
  /** Reporting period tag status (OPEN | CLOSED | PENDING); shown inline, not as a badge. */
  reportTagStatusName?: string | null;
  /** UTC API deadline; shown muted beside the report tag line. */
  reportTagDeadline?: string | null;

  // Submission/status fields from API (department_submission)
  statusName?: string | null; // SUBMITTED | NEEDS_ATTENTION etc
  notes?: string | null;
  submittedAt?: string | null;
  submittedBy?: number | null;
  submittedByName?: string | null;
  approvedAt?: string | null;
  approvedByName?: string | null;

  departmentReportId?: number | null; // meta.department_submission.department_report_id
};

type Props = {
  meta: ReportActionsPanelMeta;

  /**
   * Controls which actions show up.
   * If this is not passed, derive basic eligibility from meta.statusName
   */
  eligibility?: ReviewEligibility;

  onRequestReview?: (payload: ReviewRequestPayload) => Promise<void> | void;

  requestingReview?: boolean;
  requestReviewSuccessMessage?: string | null;
  statusLabel?: string;
  secondaryStatusName?: string | null;
  secondaryStatusLabel?: string;
  statusNote?: React.ReactNode;
  notices?: React.ReactNode;
  actions?: React.ReactNode;
  /** Optional content rendered inside the expanded Status details (e.g. submission summary). */
  statusAccessory?: React.ReactNode;
  needsAttentionReason?: string | null;
  needsAttentionDetails?: NeedsAttentionDetails | null;
};

function normalizeStatus(s?: string | null): string {
  return (s ?? "").trim().toUpperCase();
}

function defaultEligibilityFromMeta(meta: ReportActionsPanelMeta): ReviewEligibility {
  const status = normalizeStatus(meta.statusName);
  if (!status) return { eligible: false, reasons: ["Missing report status."] };

  if (!meta.departmentReportId) {
    return { eligible: false, reasons: ["Missing department report reference (departmentReportId)."] };
  }

  if (status === "SUBMITTED" || status === "AWAITING_APPROVAL") return { eligible: true };

  if (status === "APPROVED") {
    return { eligible: false, reasons: ["This report is already approved."] };
  }

  if (status === "DRAFT") {
    return {
      eligible: false,
      reasons: ["This report must be submitted before requesting review."],
    };
  }

  return {
    eligible: false,
    reasons: [`Review request is not allowed when status is ${getWorkflowStatusLabel(status)}.`],
  };
}

function formatDateTime(value?: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

export default function ReportActionsPanel({
  meta,
  eligibility,
  onRequestReview,
  requestingReview = false,
  requestReviewSuccessMessage,
  statusLabel = "Status",
  secondaryStatusName,
  secondaryStatusLabel = "Related Status",
  statusNote,
  notices,
  actions,
  statusAccessory,
  needsAttentionReason,
  needsAttentionDetails,
}: Props) {
  const displayStatus = resolveDepartmentReportDisplayStatus(
    meta.departmentReportId,
    meta.statusName
  );
  const status = normalizeStatus(displayStatus);
  const can = eligibility ?? defaultEligibilityFromMeta(meta);

  const showValidation = useErrorModalStore((s) => s.showValidation);
  const [showModal, setShowModal] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const [touched, setTouched] = React.useState(false);
  const [localError, setLocalError] = React.useState<string | null>(null);
  const [localSuccess, setLocalSuccess] = React.useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [needsAttentionOpen, setNeedsAttentionOpen] = React.useState(false);
  const currentUserId = useAuthStore((s) => s.getAuthUser()?.id ?? null);
  const reviewCopy = getRequestReviewCopy(currentUserId, meta.submittedBy);

  const reasonTrimmed = reason.trim();
  const reasonValid = reasonTrimmed.length >= 10 && reasonTrimmed.length <= 1000;

  React.useEffect(() => {
    setLocalSuccess(requestReviewSuccessMessage ?? null);
  }, [requestReviewSuccessMessage]);

  const openModal = () => {
    setLocalError(null);
    setLocalSuccess(null);
    setTouched(false);
    setReason("");
    setShowModal(true);
  };

  const closeModal = () => {
    if (requestingReview) return;
    setShowModal(false);
  };

  const handleConfirm = async () => {
    setTouched(true);
    setLocalError(null);
    setLocalSuccess(null);

    if (!onRequestReview) {
      setLocalError("Review request action is not configured.");
      return;
    }

    if (!can.eligible) {
      showValidation(can.reasons ?? ["Not eligible."]);
      return;
    }

    if (!meta.departmentReportId) {
      showValidation(["Missing department report reference."]);
      return;
    }

    if (!reasonValid) {
      showValidation(["Please provide a reason (10–1000 characters)."]);
      return;
    }

    try {
      await onRequestReview({ reason: reasonTrimmed });
      setShowModal(false);
      setLocalSuccess("Review request submitted successfully.");
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : "Failed to request review.");
    }
  };

  const submittedAt = formatDateTime(meta.submittedAt);
  const approvedAt = formatDateTime(meta.approvedAt);
  const deadlineLabel = formatReportTagDeadline(meta.reportTagDeadline);

  return (
    <>
      {/* Header row: context info left, status badge right */}
      <div className="d-flex justify-content-between align-items-start gap-3 mb-4">

        {/* Left — page title + context labels + statusNote */}
        <div className="ntcog-page-title-block mb-0">
          {meta.scopeName ? (
            <div className="mb-0" style={{ fontSize: 16 }}>
              <span className="fw-semibold">{meta.scopeLabel}:</span> {meta.scopeName}
            </div>
          ) : null}
          {meta.departmentName ? (
            <h1 className="ntcog-page-heading mb-1">
              {meta.departmentName} Department Report
            </h1>
          ) : null}
          <div className="text-muted mb-0" style={{ fontSize: "1rem", lineHeight: 1.6 }}>
            {meta.reportTagName ? (
              <div>
                <span className="fw-semibold">Report Tag:</span>{" "}
                {meta.reportTagName}
                {formatReportTagPeriodSuffix(meta.reportTagStatusName)}
                {deadlineLabel ? ` · Deadline: ${deadlineLabel}` : ""}
              </div>
            ) : null}

            {/* statusNote always visible — gives immediate workflow context */}
            {statusNote ? (
              <div className="small mt-1">{statusNote}</div>
            ) : null}
          </div>

          {/* Collapsible detail panel — toggled by clicking the status badge */}
          {detailsOpen && (
            <div className="mt-2">
              {submittedAt || approvedAt ? (
                <div className="text-muted small">
                  {submittedAt ? (
                    <>
                      <span className="fw-semibold">Submitted:</span> {submittedAt}
                      {meta.submittedByName ? ` by ${meta.submittedByName}` : null}
                    </>
                  ) : null}
                  {submittedAt && approvedAt ? " | " : null}
                  {approvedAt ? (
                    <>
                      <span className="fw-semibold">Approved:</span> {approvedAt}
                      {meta.approvedByName ? ` by ${meta.approvedByName}` : null}
                    </>
                  ) : null}
                </div>
              ) : null}

              {meta.notes ? (
                <div className="text-muted small">
                  <span className="fw-semibold">Notes:</span> {meta.notes}
                </div>
              ) : null}

              {statusAccessory ? <div>{statusAccessory}</div> : null}

              {notices ? <div className="mt-2">{notices}</div> : null}

              {localSuccess ? (
                <div className="mt-2">
                  <Alert variant="success" className="py-2 mb-0 small">
                    {localSuccess}
                  </Alert>
                </div>
              ) : null}

              {onRequestReview && !can.eligible && (can.reasons?.length ?? 0) > 0 ? (
                <div className="mt-2">
                  <Alert variant="warning" className="py-2 mb-0 small">
                    <div className="d-flex align-items-start gap-2">
                      <FiAlertTriangle style={{ marginTop: 2 }} />
                      <div>
                        <div className="fw-semibold mb-1">Review request not available</div>
                        <ul className="mb-0 ps-3">
                          {can.reasons!.map((r, i) => (
                            <li key={i}>{r}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </Alert>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Right — clickable status badge toggles the detail panel + action buttons */}
        <div className="d-flex flex-column align-items-end gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => setDetailsOpen((o) => !o)}
            className="d-flex align-items-center gap-2 border-0 bg-transparent p-0"
            aria-expanded={detailsOpen}
            title={detailsOpen ? "Hide details" : "Show details"}
            style={{ cursor: "pointer" }}
          >
            <span className="text-muted small">{statusLabel}:</span>
            <Badge {...subtleBadgeProps(getWorkflowStatusVariant(displayStatus))}>
              {getWorkflowStatusLabel(displayStatus)}
            </Badge>
            <FiChevronDown
              size={14}
              className="text-muted"
              style={{
                transition: "transform 0.2s",
                transform: detailsOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </button>

          {secondaryStatusName ? (
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">{secondaryStatusLabel}:</span>
              <Badge
                {...subtleBadgeProps(getWorkflowStatusVariant(secondaryStatusName))}
              >
                {getWorkflowStatusLabel(secondaryStatusName)}
              </Badge>
            </div>
          ) : null}

          <div className="d-flex gap-2 flex-wrap justify-content-end">
            {onRequestReview ? (
              <Button
                variant="primary"
                onClick={openModal}
                disabled={requestingReview}
              >
                <FiSend className="me-2" />
                {reviewCopy.actionLabel}
              </Button>
            ) : null}
            {actions}
          </div>
        </div>
      </div>

      {needsAttentionReason ? (
        <Alert variant="warning" className="mt-2 mb-4 py-2 px-3 small">
          <div className="fw-semibold mb-1">{getWorkflowStatusLabel("NEEDS_ATTENTION")}</div>
          <div>{needsAttentionReason}</div>
          {needsAttentionDetails ? (
            <>
              <button
                type="button"
                className="d-inline-flex align-items-center gap-1 border-0 bg-transparent p-0 mt-2 text-decoration-underline"
                aria-expanded={needsAttentionOpen}
                aria-controls="needs-attention-details"
                onClick={() => setNeedsAttentionOpen((open) => !open)}
                style={{ cursor: "pointer", color: "inherit" }}
              >
                {needsAttentionOpen ? "Hide details" : "View more details"}
                <FiChevronDown
                  size={14}
                  style={{
                    transition: "transform 0.2s",
                    transform: needsAttentionOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </button>
              {needsAttentionOpen ? (
                <div id="needs-attention-details">
                  <NeedsAttentionDetailsList details={needsAttentionDetails} />
                </div>
              ) : null}
            </>
          ) : null}
        </Alert>
      ) : null}

      <Modal show={showModal} onHide={closeModal} centered backdrop="static" size="lg">
        <Modal.Header closeButton={!requestingReview} className="border-0 pb-0">
          <Modal.Title className="fw-semibold">{reviewCopy.actionLabel}</Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-3">
          <Form onSubmit={(e) => e.preventDefault()}>
            <p className="text-muted mb-3">
              You are about to return this report for {reviewCopy.noun}. This will mark it as{" "}
              <span className="fw-semibold">{getWorkflowStatusLabel("NEEDS_ATTENTION")}</span> and require updates
              before re-submission.
            </p>

            <Form.Group className="mb-0">
            <Form.Label className="fw-medium">
              {reviewCopy.reasonLabel} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (touched) setLocalError(null);
              }}
              onBlur={() => setTouched(true)}
              placeholder="Explain what needs to be corrected..."
              disabled={requestingReview}
              isInvalid={touched && !reasonValid}
              maxLength={1000}
            />
            <div className="d-flex justify-content-between mt-1">
              <Form.Text muted>10–1000 characters.</Form.Text>
              <Form.Text muted>{reason.length} / 1000</Form.Text>
            </div>
            <Form.Control.Feedback type="invalid">
              Please provide a reason (10–1000 characters).
            </Form.Control.Feedback>
          </Form.Group>

          {localError ? (
            <Alert variant="danger" className="mt-3 mb-0">
              {localError}
            </Alert>
          ) : null}
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              variant="primary"
              size="lg"
              className="btn-action"
              onClick={handleConfirm}
              disabled={requestingReview}
              aria-busy={requestingReview}
            >
              {requestingReview ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <FiSend className="me-2" />
              )}
              {reviewCopy.confirmLabel}
            </Button>
          </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
}
